import React, { useState, useEffect, useMemo } from 'react';
import BarStack from '@/components/BarStack';
import { getThemeObj } from '@/helpers/index';
import { windowSize } from '@/recoil/atoms';
import { useRecoilState } from 'recoil';

import data from './data.json';
import SkillsList from './SkillsList';
import ThemeDetails from './ThemeDetails';
import ThemeFilters from './ThemeFilters';
import { SSkillSection, SInfo } from './styles';
import SkillDetails from './SkillDetails';

const SkillsSection = () => {
  const [view] = useRecoilState(windowSize);
  const [isMinView, setIsMinView] = useState(false);
  const [currentData, setCurrentData] = useState(data);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState({
    id: 'html',
    theme: 'htmlcss',
  });

  const { width } = view;
  const breakpoint = 768;

  const handleSkillOver = (id, theme) => {
    setHovered({ id, theme });
  };

  const handleSkillLeave = () => {
    setHovered(null);
  };

  const handleSkillSelect = (id, theme) => {
    setSelected({ id, theme });
  };

  useEffect(() => {
    setIsMinView(width < breakpoint);
  }, [width]);

  useEffect(() => {
    if (isMinView) {
      setAppliedFilters(['htmlcss']);
    }
    if (!isMinView) {
      setAppliedFilters([]);
    }
  }, [isMinView]);

  useEffect(() => {
    const newData = appliedFilters.length
      ? data.filter((d) => appliedFilters.includes(d.theme))
      : data;
    setCurrentData(newData);
  }, [appliedFilters]);

  useEffect(() => {
    setSelected({
      id: currentData[0].id,
      theme: currentData[0].theme,
    });
  }, [currentData]);

  const updateFilters = (filter) => {
    if (isMinView) {
      setAppliedFilters(filter);
    } else {
      setAppliedFilters((prevFilters) => {
        const newFilters = prevFilters.includes(filter)
          ? prevFilters.filter((f) => f !== filter)
          : [...prevFilters, filter];

        return newFilters;
      });
    }
  };

  const themes = useMemo(() => new Set(data.map((d) => d.theme)));
  const themesObj = useMemo(() =>
    [...themes].map((theme) => ({
      name: theme,
      ...getThemeObj(theme),
    }))
  );

  const skills = currentData.map((d) => ({
    id: d.id,
    name: d.name,
    theme: d.theme,
  }));

  const currentTheme = hovered ? hovered.theme : selected.theme;

  const renderSkillList = () => (
    <SkillsList
      skills={skills}
      handleSelect={handleSkillSelect}
      handleSkillOver={handleSkillOver}
      handleSkillLeave={handleSkillLeave}
      currentId={hovered ? hovered.id : selected.id}
    />
  );

  return (
    <SSkillSection data-testid="skills-section">
      <ThemeFilters
        applied={appliedFilters}
        updateFilters={updateFilters}
        themes={[...themesObj]}
      />
      {!isMinView && (
        <BarStack
          data={currentData}
          currentId={hovered ? hovered.id : selected.id}
          hovered={hovered ? hovered.id : ''}
          selected={selected.id}
          xAccessor="value"
          handleSegmentOver={handleSkillOver}
          handleSegmentLeave={handleSkillLeave}
          handleSegmentClick={handleSkillSelect}
        />
      )}
      {!isMinView && renderSkillList()}
      <SInfo data-testid="skills-info">
        <ThemeDetails theme={currentTheme} />
        {width < breakpoint && renderSkillList()}
        <SkillDetails
          skill={
            hovered
              ? data.find((d) => d.id === hovered.id)
              : data.find((d) => d.id === selected.id)
          }
          theme={currentTheme}
        />
      </SInfo>
    </SSkillSection>
  );
};

export default SkillsSection;
